import { Fragment, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ProblemError, type CalculationOut, type CaseDetailOut } from "@/api";
import { Button } from "@/components/Button";
import { Checkbox } from "@/components/Checkbox";
import { SendWarnings } from "@/components/SendWarnings";
import { useScreenToast } from "@/components/Toast";
import { formatDateTime, formatMoney } from "@/lib/format";
import { sendWarnings } from "@/lib/mailRequest";

import { forgetApprovalDraft, keepApprovalDraft, readApprovalDraft } from "./approvalDraft";
import styles from "./CalculationScreen.module.css";
import {
  acknowledgedRulesOf,
  approvalReadiness,
  approveLabel,
  count,
  groupHintsByRule,
  isReleased,
  isWallHint,
} from "./readiness";
import { refusalReasons, useApproval } from "./useApproval";

/** `intake.case_findings.CASE_WITHOUT_CUSTOMER` — the hint that gets a way to the customer. */
const CUSTOMER_RULE = "case_without_customer";

function toggled<T>(set: ReadonlySet<T>, key: T): ReadonlySet<T> {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** `ProblemError.userMessage` where there is one, else the plain `Error`
 * message — the one sentence a person (and the screen's live region) gets
 * for any failure this panel's three mutations can produce. */
function failureSentence(error: Error): string {
  return error instanceof ProblemError ? error.userMessage : error.message;
}

/** The server's reason as one sentence, so the confirmation after it starts a
 * new one — „… Preiszeile. wird von Hand bepreist" read as a typo
 * (Generalprobe 15.10.2026). */
function asSentence(text: string): string {
  const trimmed = text.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/**
 * The human signature (A-014, E-52, design §9).
 *
 * Before the approval: every wall without a measure with a box of its own
 * (E-65: „hier liegt kein Rost“ — a hint, one tick per wall, with „Alle … quittieren“
 * above them), every other hint with a box „gesehen", every price gap with a
 * box „wird von Hand bepreist" (D-24), the reasons the button is still shut,
 * and the button itself naming what it carries. After: who, when, for
 * which Stand, on which price state, with which gaps — and the two gestures
 * that remain, each in two steps and inline (no `window.confirm`): withdraw
 * with a reason, or „Angebot raus". After a withdrawal: who took it back,
 * when and why, above the form.
 *
 * Both remaining gestures are offered only while the server would actually
 * accept one — the calculation is the case's latest (not stale) and the
 * Vorgang still stands at `quote_approved` (D-12: „Angebot raus" nur aus
 * `quote_approved`; fix round 1 Important, controller notes §3 on Plan C
 * final review I-1 first covered withdraw alone). A superseded approval
 * shows the „galt für Stand n" banner instead (`CalculationBanner`) and
 * neither button here.
 *
 * Holds its own mutations and is remounted per calculation by the screen
 * (`key={calc.id}`), so a box ticked or a 409 received on one calculation
 * never carries over to the next.
 *
 * Every sentence a mutation here produces — success or failure — also goes
 * through `onDone`, the screen's one persistent live region
 * (`CalculationScreen.tsx`'s own `role="status"` line, fix round 2, item 8):
 * a fresh `role="status"` mounted already carrying its text is not reliably
 * announced, only a *change* inside a region that was already there is. The
 * visible blocks below (the reasons list, the 409 box, the plain sentence
 * under the button) stay for a sighted person to read in place; they carry
 * no `role="status"` of their own any more.
 */

export function ApprovalPanel({
  caseId,
  calc,
  detail,
  frozen,
  busy,
  onDone,
  onLeaveReleased,
}: {
  caseId: string;
  calc: CalculationOut;
  detail: CaseDetailOut;
  frozen: boolean;
  /** The screen is recalculating — nothing here may fire meanwhile. */
  busy: boolean;
  /** One sentence for the screen's live region once a gesture landed. */
  onDone: (message: string) => void;
  /**
   * Focus back to the screen's own heading once a withdrawal takes this
   * calculation out of the released branch (fix round 2, item 9) — there is
   * no "released paragraph" left to land on once the panel is back to the
   * form, unlike after an approval or a send, which stay in this branch and
   * get `releasedRef` instead.
   */
  onLeaveReleased: () => void;
}) {
  const { approve, withdraw, send } = useApproval(caseId, calc.id);
  // What the mail asked for and the offer does not hold, for the question
  // before „Angebot raus" (UX-Gutachten 4) — said, never locked.
  const warningsId = useId();
  const warnings = sendWarnings(detail.mailRequest, detail.gratingType);
  const toast = useScreenToast();
  /**
   * A release given or withdrawn (Welle 1, idea 23): said in the screen's
   * live line, as every sentence here is, and shown where the press was —
   * „Kalkulation freigeben" stands a long scroll below that line. For the
   * eye only, so it is heard once; and without „Rückgängig": a release is
   * withdrawn with a reason, in the record, not undone.
   */
  function landed(sentence: string): void {
    onDone(sentence);
    toast.show({ text: sentence, announce: false });
  }
  // What was set before „Kunde zuordnen" led to the record, given back once
  // (review final, 6).
  const [kept] = useState(() => readApprovalDraft(calc.id));
  useEffect(() => forgetApprovalDraft(calc.id), [calc.id]);
  const [seen, setSeen] = useState<ReadonlySet<string>>(() => new Set(kept?.seen ?? []));
  const [byHand, setByHand] = useState<ReadonlySet<number>>(() => new Set(kept?.byHand ?? []));
  const [note, setNote] = useState(kept?.note ?? "");
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawNote, setWithdrawNote] = useState("");
  const [sending, setSending] = useState(false);
  const releasedRef = useRef<HTMLParagraphElement>(null);

  const working = busy || approve.isPending || withdraw.isPending || send.isPending;
  const review = isReleased(calc) ? calc.review : null;
  const labelOf = (lineNo: number): string =>
    calc.lines.find((line) => line.lineNo === lineNo)?.label ?? `Zeile ${lineNo}`;

  const gestureFailure = withdraw.error ?? send.error;
  const gestureLine = gestureFailure === null ? null : failureSentence(gestureFailure);
  // Fix round 1 Important 1 / fix round 2 item 1: a failed approval that is
  // not a 409 used to be silent — `refusalReasons` answers `null` for
  // anything but a 409, and nothing else ever read `approve.error`. Every
  // failure now has a sentence, 409 or not; only the 409 case additionally
  // gets the itemised `refusal` box below.
  const approveFailureLine = approve.error === null ? null : failureSentence(approve.error);

  // Item 8: both failure sentences reach the persistent live region the
  // moment they exist, not only the visible box in this panel. Effects, not
  // an inline `onError` on `.mutate`, because `gestureLine` folds together
  // two independent mutations (`withdraw`, `send`) that must not stamp on
  // each other's `onSuccess`/`onError` handlers.
  useEffect(() => {
    if (approveFailureLine !== null) onDone(approveFailureLine);
  }, [approveFailureLine, onDone]);
  useEffect(() => {
    if (gestureLine !== null) onDone(gestureLine);
  }, [gestureLine, onDone]);

  // Item 9: focus follows the two swaps this panel makes on its own steam.
  // Tied to `review !== null` itself, not to `approve.isSuccess` directly —
  // an approval's own mutation state and the parent's re-render with the
  // written-through calculation (`useApproval`'s `settle()`) usually land
  // together, but nothing here needs to bet on that: this fires exactly
  // when the released branch starts rendering (`releasedRef` is only ever
  // attached there), whichever render that turns out to be. A withdrawal is
  // the same transition in the other direction — the screen's heading is
  // where it goes, since there is nothing of this panel's own left to land
  // on once it is back to the form.
  const justReleased = review !== null;
  const wasReleasedRef = useRef(justReleased);
  useEffect(() => {
    if (justReleased && !wasReleasedRef.current) releasedRef.current?.focus();
    if (!justReleased && wasReleasedRef.current) onLeaveReleased();
    wasReleasedRef.current = justReleased;
  }, [justReleased, onLeaveReleased]);
  // A send changes nothing about `review` — the calculation was already
  // released and stays that way — so its own `isSuccess` is the only signal
  // for it; `releasedRef` is already attached throughout, no race to bet on.
  useEffect(() => {
    if (send.isSuccess) releasedRef.current?.focus();
  }, [send.isSuccess]);

  if (review !== null) {
    const gaps = review.acknowledgedGaps;
    // D-12 / fix round 1 Important: both remaining gestures need the same
    // ground the server checks — the calculation still the case's latest,
    // the Vorgang still at `quote_approved`. A stale approval or one whose
    // Vorgang moved on shows the "galt für Stand n" banner instead
    // (`CalculationBanner`) and gets neither button here.
    const canGesture = !calc.stale && detail.status === "quote_approved";
    // `PriceStateSeenOut`'s fields are optional on the generated type (a
    // `null`-default field reads as OPTIONAL, not just nullable) — `?? null`
    // keeps a withdrawal's absent snapshot and an approval's explicit
    // `null` the same case they always were.
    const priceStateSeenSyncedAt = review.priceStateSeen.syncedAt ?? null;
    return (
      <div className={styles.approval}>
        {/* The release note (idea 19, UI wave 1), DocuSign's certificate
            rather than its event log: what was released — with every gap
            it was released with, by name — then who, when, which Stand and
            which price state. Two lines of one paragraph, so the focus that
            lands here after an approval or a send reads the whole note. The
            print's foot says the same in the same order („Freigegeben mit n
            Lücken am … von …", `features/print/sheets.ts`). */}
        <p ref={releasedRef} tabIndex={-1} className={styles.approved}>
          <span className={styles.approvedWhat}>
            <b>Freigegeben</b>
            {review.kind === "approved_with_gaps"
              ? ` mit ${count(gaps.length, "Lücke", "Lücken")}: ${gaps.map(labelOf).join(", ")}`
              : null}
          </span>{" "}
          <span className={styles.approvedFacts}>
            am <span className="tabular">{formatDateTime(review.at)}</span> von {review.by ?? "unbekannt"} · Stand{" "}
            <span className="num">{calc.planVersionNo}</span>
            {priceStateSeenSyncedAt !== null ? (
              <>
                {" "}
                · Preisstand vom <span className="tabular">{formatDateTime(priceStateSeenSyncedAt)}</span>
              </>
            ) : null}
          </span>
        </p>
        {review.note !== null ? <p className={styles.note}>„{review.note}“</p> : null}

        {frozen ? null : withdrawing ? (
          <div className={styles.inline}>
            <label className={`label ${styles.fieldLabel}`} htmlFor="withdraw-note">
              Grund
            </label>
            <textarea
              id="withdraw-note"
              className={styles.textarea}
              rows={2}
              value={withdrawNote}
              onChange={(event) => setWithdrawNote(event.target.value)}
            />
            <div className={styles.actions}>
              <Button
                variant="primary"
                disabled={working || withdrawNote.trim() === ""}
                onClick={() =>
                  withdraw.mutate(withdrawNote.trim(), {
                    onSuccess: () => {
                      setWithdrawing(false);
                      landed("Freigabe zurückgezogen.");
                    },
                  })
                }
              >
                Zurückziehen bestätigen
              </Button>
              <Button variant="quiet" disabled={working} onClick={() => setWithdrawing(false)}>
                Abbrechen
              </Button>
            </div>
          </div>
        ) : sending ? (
          <div className={styles.inline}>
            <p>Angebot als versendet markieren? Danach ist der Vorgang eingefroren.</p>
            <SendWarnings id={warningsId} warnings={warnings} />
            <div className={styles.actions}>
              <Button
                variant="primary"
                disabled={working}
                aria-describedby={warnings.length > 0 ? warningsId : undefined}
                onClick={() =>
                  send.mutate(undefined, {
                    onSuccess: () => {
                      setSending(false);
                      onDone("Angebot ist raus.");
                    },
                  })
                }
              >
                Ja, Angebot ist raus
              </Button>
              <Button variant="quiet" disabled={working} onClick={() => setSending(false)}>
                Abbrechen
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.actions}>
            {canGesture ? (
              <Button variant="primary" disabled={working} onClick={() => setSending(true)}>
                Angebot raus
              </Button>
            ) : null}
            {canGesture ? (
              <Button disabled={working} onClick={() => setWithdrawing(true)}>
                Freigabe zurückziehen
              </Button>
            ) : null}
          </div>
        )}
        {/* No `role="status"` here any more (fix round 2, item 8) — the
            effect above already put this same sentence into the screen's
            persistent live line, which is the region a screen reader is
            already listening to. This stays for a sighted person to read
            next to the buttons that just failed. */}
        {gestureLine !== null ? (
          <p className={`${styles.note} ${styles.noteBad}`}>{gestureLine}</p>
        ) : null}
      </div>
    );
  }

  const readiness = approvalReadiness(calc, detail.latestVersionNo, seen, byHand, detail.status);
  const refusal = refusalReasons(approve.error);
  const syncedAt = calc.priceSource.syncedAt;
  const approveLede =
    syncedAt !== null
      ? `Freigabe auf Preisstand vom ${formatDateTime(syncedAt)} · VK gesamt netto `
      : `Freigabe · VK gesamt netto `;
  // The reasons list gets an id only while it exists, and the button points
  // at it only then — an `aria-describedby` naming an absent id is worse
  // than none (fix round 1 Minor 3).
  const reasonsId = readiness.reasons.length > 0 ? "approval-reasons" : undefined;

  // Fix round 2, Important 3: one entry per rule, not per hint object — the
  // engine emits one hint per priced position that earns it, so two
  // positions in the same band gap send two `band_gap` hints. Grouping here
  // is what keeps the checkbox count, the React keys and `seen` (which is
  // already keyed by rule) all agreeing with each other and with the server.
  const groupedHints = groupHintsByRule(calc.hints);
  // E-65: the walls nobody measured are hints too, but they are listed apart
  // and above the rest — there can be a dozen of them, one box each, and they
  // are ticked as a kind („hier liegt kein Rost“), where the other hints are
  // each read for what they say.
  const wallHints = groupedHints.filter((hint) => isWallHint(hint.rule));
  const otherHints = groupedHints.filter((hint) => !isWallHint(hint.rule));

  // F-4 (S2-6, 15.10.2026): a withdrawal is the latest review of this
  // calculation — kept with its reason, and until now shown nowhere. Said
  // above the form that offers the approval again, in the words of the
  // cancel banner („— Grund: …"), because a cancel of a released Vorgang
  // writes this very row with the cancel's reason.
  const withdrawal = calc.review?.kind === "withdrawn" ? calc.review : null;

  return (
    <div className={styles.approval}>
      {withdrawal !== null ? (
        <p className={styles.withdrawn}>
          <b>Freigabe zurückgezogen</b> am <span className="tabular">{formatDateTime(withdrawal.at)}</span> von{" "}
          {withdrawal.by ?? "unbekannt"}
          {withdrawal.note !== null ? <> — Grund: {withdrawal.note}</> : null}
        </p>
      ) : null}

      {wallHints.length > 0 ? (
        <fieldset className={styles.checks}>
          <legend className="label">Wände ohne Maß</legend>
          {/* One press for the lot — a dozen walls is a dozen clicks otherwise —
              and idempotent where they are all ticked. Above the boxes, so it
              comes before them in the tab order, however long the list is. */}
          <Button
            variant="quiet"
            flush
            className={styles.checkAll}
            disabled={working || frozen}
            onClick={() => setSeen(new Set([...seen, ...wallHints.map((hint) => hint.rule)]))}
          >
            {wallHints.length === 1
              ? "Die Wand ohne Maß quittieren"
              : `Alle ${wallHints.length} Wände ohne Maß quittieren`}
          </Button>
          {wallHints.map((hint) => (
            <Checkbox
              key={hint.rule}
              className={styles.check}
              checked={seen.has(hint.rule)}
              disabled={working || frozen}
              onChange={() => setSeen(toggled(seen, hint.rule))}
            >
              <span>{hint.message}</span>
            </Checkbox>
          ))}
        </fieldset>
      ) : null}

      {otherHints.length > 0 ? (
        <fieldset className={styles.checks}>
          <legend className="label">Hinweise — jeden als gesehen quittieren</legend>
          {otherHints.map((hint) => (
            <Fragment key={hint.rule}>
            <Checkbox
              className={styles.check}
              checked={seen.has(hint.rule)}
              disabled={working || frozen}
              onChange={() => setSeen(toggled(seen, hint.rule))}
            >
              <span>
                {hint.message}
                {hint.lineNos.length > 0 ? (
                  <span className={styles.lineRef}> · {hint.lineNos.map(labelOf).join(", ")}</span>
                ) : null}{" "}
                <b>gesehen</b>
              </span>
            </Checkbox>
            {/* UX-Gutachten 4, NEU-2: the way to the customer, beside the
                tick — the record opens its customer dialog on `?kunde=1`, as
                the register's „Kunde zuordnen …" does. Not where the Vorgang
                takes none (cancelled, sent — review final, 6). What is set
                here is kept for the way back. */}
            {hint.rule === CUSTOMER_RULE && !frozen ? (
              <Link
                className="inline-link"
                to={`/vorgang/${caseId}?kunde=1`}
                onClick={() => keepApprovalDraft(calc.id, { seen: [...seen], byHand: [...byHand], note })}
              >
                Kunde zuordnen
              </Link>
            ) : null}
            </Fragment>
          ))}
        </fieldset>
      ) : null}

      {calc.gaps.length > 0 ? (
        <fieldset className={styles.checks}>
          <legend className="label">Lücken — jede quittieren, wird von Hand bepreist</legend>
          {calc.gaps.map((gap) => (
            <Checkbox
              key={gap.lineNo}
              className={styles.check}
              checked={byHand.has(gap.lineNo)}
              disabled={working || frozen}
              onChange={() => setByHand(toggled(byHand, gap.lineNo))}
            >
              <span>
                {labelOf(gap.lineNo)} — {asSentence(gap.message)} <b>Wird von Hand bepreist.</b>
              </span>
            </Checkbox>
          ))}
        </fieldset>
      ) : null}

      <label className={`label ${styles.fieldLabel}`} htmlFor="approve-note">
        Anmerkung (optional)
      </label>
      <textarea
        id="approve-note"
        className={styles.textarea}
        rows={2}
        value={note}
        disabled={working || frozen}
        onChange={(event) => setNote(event.target.value)}
      />

      <p className={styles.approveText}>
        {approveLede}
        <span className="num">{formatMoney(calc.vkTotal)}</span>
      </p>

      {readiness.reasons.length > 0 ? (
        <div className={styles.reasons} id={reasonsId}>
          <p className="label">Freigabe noch nicht möglich</p>
          <ul>
            {/* Index, not the text (fix round 2, item 7): two walls of the
                same length below the minimum produce the identical
                sentence, and a duplicate key is a duplicate key regardless
                of how unlikely the collision looks today. */}
            {readiness.reasons.map((reason, index) => (
              <li key={index}>{reason}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* No `role="status"` here any more (fix round 2, item 8) — this same
          409 sentence already reaches the screen's persistent live line
          through the effect above; the box stays for a sighted person to
          read the itemised list. */}
      {refusal !== null ? (
        <div className={`${styles.reasons} ${styles.reasonsRefused}`}>
          <p className="label">Nicht freigegeben</p>
          <ul>
            {/* Index, not the text (fix round 2, item 7) — the server's own
                `reasons` list can repeat a sentence for the same reason the
                one above can. */}
            {refusal.map((reason, index) => (
              <li key={index}>{reason}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Fix round 1 Important 1 / fix round 2 item 1: a failure that is not
          a 409 has no itemised box above — without this, the button simply
          went enabled again with nothing said about why it had refused. */}
      {refusal === null && approveFailureLine !== null ? (
        <p className={`${styles.note} ${styles.noteBad}`}>{approveFailureLine}</p>
      ) : null}

      <div className={styles.actions}>
        <Button
          variant="primary"
          busy={approve.isPending}
          disabled={working || frozen || !readiness.canApprove}
          aria-describedby={reasonsId}
          onClick={() => {
            // Fix round 2, Important 4 (controller ruling): no guard on
            // `vkTotal` any more — the server's own `approvable()` accepts
            // `expected_vk_total: null` against a `vk_total` that is also
            // `null` (`None == None`), the one shape D-24 names for a
            // calculation with no priced line at all. The generated type
            // already allows it.
            approve.mutate(
              {
                expectedVkTotal: calc.vkTotal,
                note: note.trim() === "" ? null : note.trim(),
                // Only what this calculation carries: a tick list can outlive
                // its calculation (the draft kept across „Kunde zuordnen“),
                // and the release record stores whatever is sent.
                acknowledgedRules: acknowledgedRulesOf(calc, seen),
                acknowledgedGaps: [...byHand],
              },
              { onSuccess: () => landed("Freigegeben.") },
            );
          }}
        >
          {approveLabel(readiness.gapCount)}
        </Button>
      </div>
    </div>
  );
}
